import { orderBy } from 'natural-orderby';
import {
  algoHasBrokerDisabled,
  algoHasError,
  algoHasStoplossTrailing,
  algoHasWaiting,
} from '../../algo-errors';
import {
  INTRADAY,
  POSITIONAL,
  POSITIONAL_TYPE_N_DAYS,
  POSITIONAL_TYPE_NEXT_DAY,
} from '@quantiply/common/constants';

export const sortByFieldNameAlphabetically = (data, field, order = 'ASC') => {
  let newData = [...data];

  newData = newData.sort((a, b) => {
    const posA = a[field] ? a[field].toLowerCase() : '';
    const posB = b[field] ? b[field].toLowerCase() : '';

    if (posA < posB) return -1;
    if (posA > posB) return 1;

    return 0;
  });

  if (order === 'DSC') {
    newData.reverse();
  }

  return newData;
};

export const sortNumeric = (data, field, order = 'ASC') => {
  let newData = [...data];

  let newDataValues = newData.filter((nd) => nd[field] || nd[field] === 0);
  const newDataBlank = newData.filter((nd) => !nd[field] && nd[field] !== 0);

  newDataValues = newDataValues.sort((a, b) => {
    const posA = a[field] ? a[field] : '';
    const posB = b[field] ? b[field] : '';

    return posB - posA;
  });

  if (order === 'DSC') {
    newDataValues.reverse();
  }

  newData = [...newDataValues, ...newDataBlank];

  return newData;
};

export const getSortedUnderlyingSymbolsCountwise = (data) => {
  if (!data.length) {
    return [];
  }

  let newData = [...data];

  const newDataValues = newData.sort((a, b) => {
    const posA = a.underlyings.length ? a.underlyings.length : '';
    const posB = b.underlyings.length ? b.underlyings.length : '';

    return posB - posA;
  });

  return newDataValues;
};

const filterStatus = (data, statusToFind) =>
  data.filter(({ status = 'NOT_RUNNING' }) => status === statusToFind);

export const sortStatus = (data, order = 'ASC') => {
  // 1. Trade Active
  // 2. Trade Closed
  // 3. Ready
  // 4. Enabled
  // 5. Disabled

  const filterListEnabled =
    data.filter(
      ({ status = 'NOT_RUNNING', enabled = false }) =>
        status === 'NOT_RUNNING' && enabled
    ) || [];
  const filterListDisabled =
    data.filter(
      ({ status = 'NOT_RUNNING', enabled = false }) =>
        status === 'NOT_RUNNING' && !enabled
    ) || [];
  const filterListTradeActive = filterStatus(data, 'TRADE_ACTIVE') || [];
  const filterListReady = filterStatus(data, 'READY') || [];
  const filterListTradeClosed = filterStatus(data, 'TRADE_CLOSED') || [];

  let newData = [
    ...filterListTradeActive,
    ...filterListTradeClosed,
    ...filterListReady,
    ...filterListEnabled,
    ...filterListDisabled,
  ];

  if (order === 'DSC') {
    newData = [
      ...filterListDisabled,
      ...filterListEnabled,
      ...filterListReady,
      ...filterListTradeClosed,
      ...filterListTradeActive,
    ];
  }

  return newData;
};

export const sortByFieldNameBoolen = (data, field, order = 'ASC') => {
  let newData = [...data];

  newData = newData.sort((a, b) => b[field] - a[field]);

  if (order === 'DSC') {
    newData.reverse();
  }

  return newData;
};

export const sortAlgoStatus = (data, order = 'ASC') => {
  if (!data || (data && !data.length)) {
    return data;
  }

  const green = [];
  const orange = [];
  const red = [];
  const grey = [];

  for (let index = 0; index < data.length; index++) {
    const element = data[index];
    const { algoStatus = '' } = element || {};

    switch (algoStatus) {
      case 'GREEN':
      case 'RUNNING':
        green.push(element);
        break;
      case 'ORANGE':
        orange.push(element);
        break;
      case 'RED':
      case 'NOT_RUNNING':
        red.push(element);
        break;
      default:
        grey.push(element);
    }
  }

  let newData = [...green, ...orange, ...red, ...grey];

  if (order === 'DSC') {
    newData = [...red, ...orange, ...green, ...grey];
  }

  return newData;
};

export const sortBroker = (data, order = 'ASC', brokersDataAll = []) => {
  const filterWithBrokers =
    data
      .map((d = {}) => {
        if (!d.broker) {
          return null;
        }

        const { label: brokerLabel = '' } =
          brokersDataAll.find(
            (bda) =>
              bda.value && bda.value.toLowerCase() === d.broker.toLowerCase()
          ) || {};

        if (!brokerLabel) {
          return null;
        }

        d.brokerLabel = brokerLabel;

        return d;
      })
      .filter((d) => d) || [];

  const filterWithOutBrokers =
    data.filter((d = {}) => {
      if (!d.broker) {
        return d;
      }

      const { label: brokerLabel = '' } =
        brokersDataAll.find(
          (bda) =>
            bda.value && bda.value.toLowerCase() === d.broker.toLowerCase()
        ) || {};

      if (!brokerLabel) {
        return d;
      }

      return null;
    }) || [];

  const orderInternal = order === 'DSC' ? 'desc' : 'asc';

  const filterWithBrokersSpecial = orderBy(
    filterWithBrokers,
    [(v) => v.brokerLabel],
    [orderInternal]
  );

  const newData = [...filterWithBrokersSpecial, ...filterWithOutBrokers];

  return newData;
};

export const sortAlerts = (data, order = 'ASC', brokersDataAll = []) => {
  const filterHasError = data.filter(algoHasError) || [];

  const filterHasWaiting =
    data.filter((d = {}) => {
      const hasError = algoHasError(d);
      const hasWaiting = algoHasWaiting(d);

      if (!hasError && hasWaiting) {
        return d;
      }

      return null;
    }) || [];

  const filterHasStoplossTrailing =
    data.filter((d = {}) => {
      const hasError = algoHasError(d);
      const hasWaiting = algoHasWaiting(d);
      const hasStoplossTrailing = algoHasStoplossTrailing(d);

      if (!hasError && !hasWaiting && hasStoplossTrailing) {
        return d;
      }

      return null;
    }) || [];

  const filterBrokerDisabled =
    data.filter((d = {}) => {
      const hasError = algoHasError(d);
      const hasWaiting = algoHasWaiting(d);
      const hasStoplossTrailing = algoHasStoplossTrailing(d);
      const hasBrokerDisable = algoHasBrokerDisabled(d, brokersDataAll);

      if (
        !hasError &&
        !hasWaiting &&
        !hasStoplossTrailing &&
        hasBrokerDisable
      ) {
        return d;
      }

      return null;
    }) || [];

  const filterClear =
    data.filter((d = {}) => {
      const hasError = algoHasError(d);
      const hasWaiting = algoHasWaiting(d);
      const hasStoplossTrailing = algoHasStoplossTrailing(d);
      const hasBrokerDisable = algoHasBrokerDisabled(d, brokersDataAll);

      if (
        !hasError &&
        !hasWaiting &&
        !hasStoplossTrailing &&
        !hasBrokerDisable
      ) {
        return d;
      }

      return null;
    }) || [];

  let filteredData = [
    ...filterHasError,
    ...filterHasWaiting,
    ...filterHasStoplossTrailing,
    ...filterBrokerDisabled,
  ];

  if (order === 'DSC') {
    filteredData.reverse();
  }

  let newData = [...filteredData, ...filterClear];

  return newData;
};

export const sortStringField = (data, fieldName, order = 'ASC') => {
  let newData = [...data];
  const blankFields = newData.filter((d) => !d[fieldName]);
  const targetFields = newData.filter((d) => d[fieldName]);

  const targetFieldsSorted = targetFields.sort((a, b) => {
    const posA = a[fieldName] ? a[fieldName].toLowerCase() : '';

    const posB = b[fieldName] ? b[fieldName].toLowerCase() : '';

    if (posA < posB) return -1;
    if (posA > posB) return 1;

    return 0;
  });

  if (order === 'DSC') {
    targetFieldsSorted.reverse();
  }

  newData = [...targetFieldsSorted, ...blankFields];

  return newData;
};

export const sortTradingType = (data, order = 'ASC') => {
  let newData = [...data];
  const blankFields = newData.filter((d) => !d?.tradingType);
  const intradDayFields = newData.filter((d) => d?.tradingType === INTRADAY);

  const positionalFields = newData.filter((d) => d?.tradingType === POSITIONAL);

  const positionalTypeNextDay = newData.filter(
    (d) => d?.tradingType === POSITIONAL_TYPE_NEXT_DAY
  );

  const positionalTypeNDays = newData.filter(
    (d) => d?.tradingType === POSITIONAL_TYPE_N_DAYS
  );

  newData =
    order === 'DSC'
      ? [
          ...positionalTypeNDays,
          ...positionalTypeNextDay,
          ...positionalFields,
          ...intradDayFields,
          ...blankFields,
        ]
      : [
          ...intradDayFields,
          ...positionalFields,
          ...positionalTypeNextDay,
          ...positionalTypeNDays,
          ...blankFields,
        ];

  return newData;
};

export const sortUnderlyingSymbol = (data, order = 'ASC') => {
  //  List of labels:
  // 1. Banknifty
  // 2. Nifty
  // 3. Finnifty
  // 4. Sensex
  // 5. Midcpnifty
  // 6. Bankex

  let newData = [...data];

  const blankFields = newData.filter(
    (d) => !d.underlyings || (d.underlyings && !d.underlyings?.length)
  );

  const bankniftyFields = newData.filter(
    (d) => d.underlyings?.length === 1 && d.underlyings?.includes('BANKNIFTY')
  );

  const niftyFields = newData.filter(
    (d) => d.underlyings?.length === 1 && d.underlyings?.includes('NIFTY')
  );

  const finniftyFields = newData.filter(
    (d) => d.underlyings?.length === 1 && d.underlyings?.includes('FINNIFTY')
  );

  const sensexFields = newData.filter(
    (d) => d.underlyings?.length === 1 && d.underlyings?.includes('SENSEX')
  );

  const midcpniftyFields = newData.filter(
    (d) => d.underlyings?.length === 1 && d.underlyings?.includes('MIDCPNIFTY')
  );

  const bankexFields = newData.filter(
    (d) => d.underlyings?.length === 1 && d.underlyings?.includes('BANKEX')
  );

  const fieldsWithMultiple = newData.filter((d) => d.underlyings?.length > 1);

  const filteredFieldsWithMultiple =
    getSortedUnderlyingSymbolsCountwise(fieldsWithMultiple);

  const singleFields = [
    ...bankniftyFields,
    ...niftyFields,
    ...finniftyFields,
    ...sensexFields,
    ...midcpniftyFields,
    ...bankexFields,
  ];

  let allUnderlyingFields = [];

  if (order === 'ASC') {
    allUnderlyingFields = [...filteredFieldsWithMultiple, ...singleFields];
  } else {
    allUnderlyingFields = [...singleFields, ...filteredFieldsWithMultiple];
  }

  newData = [...allUnderlyingFields, ...blankFields];

  return newData;
};

export const sortTransactionType = (data, order = 'ASC') => {
  //  List of labels:
  // 1. Buy: If all legs configured inside the algo are Buy legs, then this label will show.
  // 2. Sell: If all legs configured inside the algo are Sell legs, then this label will show.
  // 3. Buy / Sell: This label will show when there are sell and buy legs both configured in the algo.

  let newData = [...data];
  const blankFields = newData.filter((d) => !d.transactionTypes.length);
  const buyFields = newData.filter(
    (d) => d.transactionTypes.includes('B') && !d.transactionTypes.includes('S')
  );
  const sellFields = newData.filter(
    (d) => d.transactionTypes.includes('S') && !d.transactionTypes.includes('B')
  );
  const bothFields = newData.filter(
    (d) => d.transactionTypes.includes('S') && d.transactionTypes.includes('B')
  );

  const allTransactionTypeFields = [...buyFields, ...sellFields, ...bothFields];

  if (order === 'DSC') {
    allTransactionTypeFields.reverse();
  }

  newData = [...allTransactionTypeFields, ...blankFields];

  return newData;
};

export const sortByType = (data, order = 'ASC') => {
  let newData = [...data];
  const noType = newData.filter((d) => !d?.type || d?.type !== 'SIGNAL');
  const signalType = newData.filter((d) => d?.type === 'SIGNAL');

  const processedData = [...signalType, ...noType];

  if (order === 'DSC') {
    processedData.reverse();
  }

  return processedData;
};

export const sortingConditional = (data, sort, brokersDataAll) => {
  if (!sort || (sort && !sort.field) || (sort && !sort.order)) {
    return data;
  }

  let newData = [];

  switch (sort.field) {
    case 'name':
      newData = sortByFieldNameAlphabetically(data, sort.field, sort.order);
      break;
    case 'pnl':
      newData = sortNumeric(data, sort.field, sort.order);
      break;
    case 'status':
      newData = sortStatus(data, sort.order);
      break;
    case 'algo-status':
      newData = sortAlgoStatus(data, sort.order);
      break;
    case 'broker':
      newData = sortBroker(data, sort.order, brokersDataAll);
      break;
    case 'alerts':
      newData = sortAlerts(data, sort.order, brokersDataAll);
      break;
    case 'startTime':
      newData = sortStringField(data, sort.field, sort.order);
      break;
    case 'endTime':
      newData = sortStringField(data, sort.field, sort.order);
      break;
    case 'tradingType':
      newData = sortTradingType(data, sort.order);
      break;
    case 'transactionType':
      newData = sortTransactionType(data, sort.order);
      break;
    case 'multiplier':
      newData = sortNumeric(data, sort.field, sort.order);
      break;
    case 'underlyingSymbols':
      newData = sortUnderlyingSymbol(data, sort.order);
      break;
    case 'type':
      newData = sortByType(data, sort.order);
      break;
    case 'entryErrorCount':
      newData = sortNumeric(data, sort.field, sort.order);
      break;
    case 'exitErrorCount':
      newData = sortNumeric(data, sort.field, sort.order);
      break;
    default:
      newData = data;
  }

  return newData;
};
